import { TransportType } from '@libs/trip-planner-interfaces';
import type {
  HotelIngredientAdapter,
  TransportIngredientAdapter,
  OwnStayIngredientAdapter,
  OwnTransportIngredientAdapter,
} from '@libs/trip-planner-interfaces';
import { exhaustiveGuard } from '@libs/utils';

import type { RecipeMainIngredient } from '../../recipe/ingredients';
import {
  isFerryIngredient,
  isFlightIngredient,
  isHotelIngredient,
  isOwnStayIngredient,
  isOwnTransportIngredient,
  isBookableTransportIngredient,
} from '../../recipe/ingredients';
import {
  adaptFerryIngredientToItinerarySearchCriteria,
  adaptFlightIngredientToItinerarySearchCriteria,
} from '../ingredientAdapters/itinerarySearchCriteria/transportIngredientItinerarySearchAdapter';
import { adaptHotelIngredientToItinerarySearchCriteria } from '../ingredientAdapters/itinerarySearchCriteria/hotelIngredientItinerarySearchAdapter';
import { adaptOwnStayIngredientToItinerarySearchCriteria } from '../ingredientAdapters/itinerarySearchCriteria/ownStayIngredientItinerarySearchAdapter';
import { adaptOwnTransportIngredientToItinerarySearchCriteria } from '../ingredientAdapters/itinerarySearchCriteria/ownTransportIngredientItinerarySearchAdapter';

export function adaptRecipeIngredientsToItinerarySearchCriteria(
  recipe: RecipeMainIngredient[]
):
  | (
      | OwnStayIngredientAdapter
      | HotelIngredientAdapter
      | TransportIngredientAdapter
      | OwnTransportIngredientAdapter
    )[]
  | null {
  const missingPorts: boolean = recipe.some(
    (ingredient: RecipeMainIngredient) =>
      isBookableTransportIngredient(ingredient) &&
      (!ingredient.fromPort || !ingredient.toPort)
  );

  if (missingPorts) {
    return null;
  }

  const filteredRecipe = recipe.filter(
    (ingredient) =>
      !(
        isOwnTransportIngredient(ingredient) &&
        ingredient.ownTransportType === TransportType.None
      )
  );

  return filteredRecipe.map((ingredient: RecipeMainIngredient) => {
    if (isFlightIngredient(ingredient)) {
      return adaptFlightIngredientToItinerarySearchCriteria(ingredient);
    }

    if (isFerryIngredient(ingredient)) {
      return adaptFerryIngredientToItinerarySearchCriteria(ingredient);
    }

    if (isHotelIngredient(ingredient)) {
      return adaptHotelIngredientToItinerarySearchCriteria(ingredient);
    }

    if (isOwnStayIngredient(ingredient)) {
      return adaptOwnStayIngredientToItinerarySearchCriteria(ingredient);
    }

    if (isOwnTransportIngredient(ingredient)) {
      return adaptOwnTransportIngredientToItinerarySearchCriteria(ingredient);
    }

    return exhaustiveGuard(ingredient, 'Is not Recipe Main Ingredient');
  });
}
